import { Modal } from '@constants/Themed';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import moment from 'moment';
import React from 'react';
import { DateRangePicker } from 'react-date-range';
import { View, Platform, StyleSheet, Pressable, Dimensions } from 'react-native';
import { FontAwesome, MaterialIcons } from '@expo/vector-icons';
import { Text } from 'tamagui';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import DatePicker from 'react-native-date-picker';
import * as Animatable from 'react-native-animatable';
import { LinearGradient } from 'expo-linear-gradient';

interface DefaultDates {
  noIcons?: boolean;
  startDate: Date | string | null;
  endDate: Date | string | null;
  forceColumn?: boolean;
  dateCallback?: (date: { startDate: Date, endDate: Date }) => void;
}

const RangePicker = ({ forceColumn, noIcons, startDate, endDate, dateCallback }: DefaultDates) => {
  const theme = themeSelector();
  const [isVisible, setIsVisible] = React.useState(false);
  const [startDateVisible, setStartDateVisible] = React.useState(false);
  const [endDateVisible, setEndDateVisible] = React.useState(false);
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);
  const [screenWidth, setScreenWidth] = React.useState(Dimensions.get('window').width);

  // Responsive breakpoints
  const isMobile = screenWidth < 768;
  const isTablet = screenWidth >= 768 && screenWidth < 1024;

  // Responsive dimension tracking
  React.useEffect(() => {
    // Native and web dimension listener
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenWidth(window.width);
    });

    // Web-specific resize listener for better responsiveness
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleResize = () => {
        setScreenWidth(window.innerWidth);
      };
      window.addEventListener('resize', handleResize);
      return () => {
        subscription?.remove();
        window.removeEventListener('resize', handleResize);
      };
    }

    return () => {
      subscription?.remove();
    };
  }, []);

  // Validate and normalize dates
  const validateDate = (date: Date | undefined | null): Date => {
    if (!date) return new Date();
    const d = date instanceof Date ? date : new Date(date);
    return isNaN(d.getTime()) ? new Date() : d;
  };

  const validStartDate = validateDate(startDate);
  const validEndDate = validateDate(endDate);

  const [dateVals, setDateVals] = React.useState({
    startDate: validStartDate,
    endDate: validEndDate,
  });

  const [dateRange, setDateRange] = React.useState({
    startDate: validStartDate,
    endDate: validEndDate,
    key: 'selectionChart'
  });

  // Update dateVals and dateRange when props change, only if they are actually different
  React.useEffect(() => {
    const newStart = validateDate(startDate);
    const newEnd = validateDate(endDate);

    const isStartDateChanged = dateVals.startDate.getTime() !== newStart.getTime();
    const isEndDateChanged = dateVals.endDate.getTime() !== newEnd.getTime();

    if (isStartDateChanged || isEndDateChanged) {
      setDateVals({
        startDate: newStart,
        endDate: newEnd,
      });
      setDateRange({
        startDate: newStart,
        endDate: newEnd,
        key: 'selectionChart'
      });
    }
  }, [startDate, endDate]);

  const handlePress = (type: string) => {
    if (Platform.OS === "web") {
      setIsVisible(true);
    } else {
      if (type === "start") {
        setStartDateVisible(true);
      } else {
        setEndDateVisible(true);
      }
    }
  };

  const handleDateConfirm = (date: Date, type: 'start' | 'end') => {
    if (type === 'start') {
      const newVals = { ...dateVals, startDate: date };
      setDateVals(newVals);
      dateCallback?.({ startDate: date, endDate: newVals.endDate });
    } else {
      const newVals = { ...dateVals, endDate: date };
      setDateVals(newVals);
      dateCallback?.({ startDate: newVals.startDate, endDate: date });
    }
  };

  // Gradient colors matching Hero.tsx
  const gradientColors = theme
    ? ['rgba(200, 162, 200, 0.15)', 'rgba(135, 206, 235, 0.15)']
    : ['rgba(200, 162, 200, 0.25)', 'rgba(135, 206, 235, 0.25)'];

  const containerBgColor = theme
    ? 'rgba(255, 255, 255, 0.95)'
    : 'rgba(30, 41, 59, 0.95)';

  return (
    <View 
      style={[
        modernStyles.container,
        {
          borderColor: theme ? 'rgba(59, 130, 246, 0.15)' : 'rgba(255, 255, 255, 0.15)',
        }
      ]}
    >
      {/* Floating Blur Elements - Matching Hero.tsx */}
      {Platform.OS === 'web' && (
        <>
          <View style={[modernStyles.floatingBlur, { top: -20, left: -20 }]} />
          <View style={[modernStyles.floatingBlur, { bottom: -20, right: -20 }]} />
        </>
      )}
      {/* Web Date Range Picker Modal */}
      {Platform.OS === 'web' && (
        <Modal
          setVisible={setIsVisible}
          animationIn="slideInUp"
          animationOut="slideOutDown"
          animationInTiming={300}
          animationOutTiming={200}
          backdropOpacity={0.5}
          backdropColor="#000"
          backdropTransitionInTiming={300}
          backdropTransitionOutTiming={200}
          hasBackdrop={true}
          avoidKeyboard={true}
          coverScreen={true}
          useNativeDriver={true}
          useNativeDriverForBackdrop={true}
          hideModalContentWhileAnimating={true}
          propagateSwipe={false}
          onBackButtonPress={() => setIsVisible(false)}
          onBackdropPress={() => setIsVisible(false)}
          onSwipeComplete={() => setIsVisible(false)}
          isVisible={isVisible}
          containerStyle={{ width: "100%" }}
          style={[
            modernStyles.modalContainer,
            {
              minWidth: isMobile ? '90%' : isTablet ? 600 : 700,
              maxWidth: isMobile ? '95%' : 800,
            }
          ]}
        >
          <View style={modernStyles.modalContent}>
            {/* Modal Gradient Background */}
            <LinearGradient
              colors={gradientColors}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={modernStyles.modalGradient}
            />

            {/* Close Button */}
            <Animatable.View animation="fadeIn" delay={200} style={modernStyles.closeButtonContainer}>
              <Pressable
                style={modernStyles.closeButton}
                onPress={() => setIsVisible(false)}
              >
                <Text style={modernStyles.closeButtonText}>Close</Text>
              </Pressable>
            </Animatable.View>

            {/* Date Range Picker */}
            <View style={modernStyles.pickerContainer}>
              <DateRangePicker
                style={{ flex: 1 }}
                ranges={[{
                  startDate: dateVals.startDate,
                  endDate: dateVals.endDate,
                  key: 'selectionChart'
                }]}
                onCanceled={() => setIsVisible(false)}
                onChange={(ranges) => {
                  const { selectionChart } = ranges;
                  const validatedStart = moment(selectionChart.startDate).startOf('day').toDate();
                  const validatedEnd = moment(selectionChart.endDate).endOf('day').toDate();
                  setDateRange({
                    startDate: validatedStart,
                    endDate: validatedEnd,
                    key: 'selectionChart'
                  });
                  setDateVals({
                    startDate: validatedStart,
                    endDate: validatedEnd,
                  });
                  dateCallback?.({ startDate: validatedStart, endDate: validatedEnd });
                  setIsVisible(false);
                }}
              />
            </View>
          </View>
        </Modal>
      )}

      {/* Mobile Date Pickers */}
      {Platform.OS !== 'web' && (
        <>
          <DatePicker
            modal
            open={startDateVisible}
            mode="date"
            date={dateVals.startDate}
            onConfirm={(date) => {
              setStartDateVisible(false);
              handleDateConfirm(date, 'start');
            }}
            onCancel={() => setStartDateVisible(false)}
          />
          <DatePicker
            modal
            open={endDateVisible}
            mode="date"
            date={dateVals.endDate}
            onConfirm={(date) => {
              setEndDateVisible(false);
              handleDateConfirm(date, 'end');
            }}
            onCancel={() => setEndDateVisible(false)}
          />
        </>
      )}
      {/* Date Buttons Container */}
      <View style={modernStyles.contentContainer}>
        {!noIcons ? (
          <Animatable.View 
            animation="fadeIn"
            delay={300}
            style={[
              modernStyles.dateButtonContainer,
              !theme && modernStyles.dateButtonContainerDark,
              isMobile && modernStyles.dateButtonContainerMobile
            ]}
          >
            <View style={modernStyles.iconContainer}>
              <MaterialIcons 
                name="event" 
                size={isMobile ? 18 : 20} 
                color={theme ? '#3B82F6' : '#60A5FA'} 
              />
            </View>
            
            <Pressable 
              style={[
                modernStyles.dateButton,
                modernStyles.startButton,
                isMobile && modernStyles.dateButtonMobile
              ]}
              onPress={() => handlePress('start')}
            >
              <Text style={[
                modernStyles.dateButtonText,
                isMobile && modernStyles.dateButtonTextMobile
              ]}>
                {moment(dateVals.startDate).format(globalDate).toString()}
              </Text>
            </Pressable>
            
            <Pressable 
              style={[
                modernStyles.dateButton,
                modernStyles.endButton,
                isMobile && modernStyles.dateButtonMobile
              ]}
              onPress={() => handlePress('end')}
            >
              <Text style={[
                modernStyles.dateButtonText,
                isMobile && modernStyles.dateButtonTextMobile
              ]}>
                {moment(dateVals.endDate).format(globalDate).toString()}
              </Text>
            </Pressable>
            
            <View style={modernStyles.iconContainer}>
              <FontAwesome 
                name="calendar-check-o" 
                size={isMobile ? 16 : 18} 
                color={theme ? '#10B981' : '#34D399'} 
              />
            </View>
          </Animatable.View>
        ) : (
          <Animatable.View 
            animation="fadeIn"
            delay={300}
            style={[
              modernStyles.noIconsContainer,
              { flexDirection: forceColumn || isMobile ? 'column' : 'row' }
            ]}
          >
            <Pressable 
              style={[
                modernStyles.dateButton, 
                modernStyles.startButton,
                  forceColumn || isMobile ? modernStyles.dateButtonColumn : modernStyles.dateButtonRow,
              ]}
              onPress={() => handlePress('start')}
            >
                <Text style={[
                  modernStyles.dateButtonText,
                  isMobile && modernStyles.dateButtonTextMobile
                ]}>
                  {moment(dateVals.startDate).format(globalDate).toString()}
              </Text>
            </Pressable>
            
            <Pressable 
              style={[
                modernStyles.dateButton, 
                modernStyles.endButton,
                  forceColumn || isMobile ? modernStyles.dateButtonColumn : modernStyles.dateButtonRow,
              ]}
              onPress={() => handlePress('end')}
            >
                <Text style={[
                  modernStyles.dateButtonText,
                  isMobile && modernStyles.dateButtonTextMobile
                ]}>
                  {moment(dateVals.endDate).format(globalDate).toString()}
              </Text>
            </Pressable>
          </Animatable.View>
        )}
      </View>
    </View >
  )
}

// Modern Professional Styles - Matching Hero.tsx Design System
const modernStyles = StyleSheet.create({
  container: {
    flex: 1.5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 5,
    borderRadius: 24,
    marginHorizontal: -20,
    paddingHorizontal: Platform.select({ web: 16, default: 12 }),
    paddingVertical: Platform.select({ web: 12, default: 8 }),
    overflow: 'hidden',
    position: 'relative',
  },

  gradientBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },

  floatingBlur: {
    position: 'absolute',
    width: 128,
    height: 128,
    borderRadius: 64,
    backgroundColor: Platform.OS === 'web' ? 'rgba(200, 162, 200, 0.1)' : 'transparent',
    ...(Platform.OS === 'web' && {
      filter: 'blur(40px)',
    }),
    zIndex: 1,
  },

  contentContainer: {
    position: 'relative',
    zIndex: 3,
    width: '100%',
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
    }),
  },

  dateButtonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 20,
    paddingHorizontal: Platform.select({ web: 16, default: 12 }),
    paddingVertical: Platform.select({ web: 8, default: 6 }),
    minWidth: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 6,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(10px)',
    }),
  },

  dateButtonContainerDark: {
    backgroundColor: 'rgba(30, 41, 59, 0.9)',
  },

  dateButtonContainerMobile: {
    flexWrap: 'wrap',
    paddingHorizontal: 8,
    paddingVertical: 6,
  },

  dateButton: {
    flex: 1,
    paddingHorizontal: Platform.select({ web: 16, default: 12 }),
    paddingVertical: Platform.select({ web: 12, default: 10 }),
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: Platform.select({ web: 4, default: 2 }),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
    minHeight: Platform.select({ web: 44, default: 40 }),
  },

  dateButtonMobile: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginHorizontal: 2,
    minHeight: 36,
  },

  dateButtonRow: {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },

  dateButtonColumn: {
    borderRadius: 16,
    marginBottom: 8,
    width: '100%',
  },

  startButton: {
    backgroundColor: '#3B82F6',
  },

  endButton: {
    backgroundColor: '#10B981',
  },

  dateButtonText: {
    fontSize: Platform.select({ web: 13, default: 12 }),
    fontWeight: '600',
    color: 'white',
    textAlign: 'center',
  },

  dateButtonTextMobile: {
    fontSize: 11,
  },

  iconContainer: {
    paddingHorizontal: Platform.select({ web: 8, default: 6 }),
    alignItems: 'center',
    justifyContent: 'center',
  },

  modalContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    borderRadius: 24,
    padding: Platform.select({ web: 24, default: 16 }),
    minHeight: Platform.select({ web: 500, default: 400 }),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.25,
    shadowRadius: 24,
    elevation: 16,
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.15)',
    overflow: 'hidden',
    position: 'relative',
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
      boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
    }),
  },

  modalContent: {
    position: 'relative',
    zIndex: 1,
    flex: 1,
  },

  modalGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },

  closeButtonContainer: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 10,
  },

  closeButton: {
    backgroundColor: '#EF4444',
    borderRadius: 12,
    paddingHorizontal: 20,
    paddingVertical: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
  },

  closeButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },

  pickerContainer: {
    flex: 1,
    marginTop: 50,
    zIndex: 2,
  },

  noIconsContainer: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    padding: Platform.select({ web: 4, default: 2 }),
  },
});

export default RangePicker; 